import React from 'react';
import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import './ErrorBoundary.styles.scss';

const isDev = process.env.NODE_ENV === 'development';

// ── Shared fallback UI ────────────────────────────────────────────────────────
export const ErrorFallback = ({ title, message, detail, onReload, onHome, onRetry }) => (
  <div className="error-boundary" role="alert">
    <div className="error-boundary__card">
      <span className="error-boundary__icon">
        <ErrorOutlineIcon />
      </span>
      <h1 className="error-boundary__title">{title || 'Something went wrong'}</h1>
      <p className="error-boundary__text">
        {message || 'An unexpected error occurred. You can retry, reload the page, or head back home.'}
      </p>

      {isDev && detail && (
        <pre className="error-boundary__detail">{detail}</pre>
      )}

      <div className="error-boundary__actions">
        {onRetry && (
          <button type="button" className="error-boundary__btn error-boundary__btn--primary" onClick={onRetry}>
            Try again
          </button>
        )}
        {onReload && (
          <button type="button" className="error-boundary__btn error-boundary__btn--primary" onClick={onReload}>
            <RefreshIcon fontSize="small" /> Reload page
          </button>
        )}
        {onHome && (
          <button type="button" className="error-boundary__btn error-boundary__btn--ghost" onClick={onHome}>
            <HomeOutlinedIcon fontSize="small" /> Go to home
          </button>
        )}
      </div>
    </div>
  </div>
);

// ── Route-level error element (for errors thrown inside route components) ──────
export const RouteError = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred while loading this page.';

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found';
      message = 'The page you’re looking for doesn’t exist or has moved.';
    } else {
      title = `${error.status} ${error.statusText || ''}`.trim();
      message = error.data?.message || message;
    }
  }

  const detail = error?.stack || error?.message || (typeof error === 'string' ? error : null);

  return (
    <ErrorFallback
      title={title}
      message={message}
      detail={detail}
      onReload={() => window.location.reload()}
      onHome={() => navigate('/')}
    />
  );
};

// ── Top-level class error boundary (catches render errors in the tree) ─────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Hook point for an error-reporting service (Sentry, etc.).
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info?.componentStack);
  }

  handleRetry = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          detail={this.state.error?.stack || this.state.error?.message}
          onRetry={this.handleRetry}
          onReload={() => window.location.reload()}
          onHome={() => { window.location.href = '/'; }}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
